'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useMemo, useState, useEffect } from 'react';
import { getLocalizedPath, getLocalizedPagePath } from '@/lib/locale-utils';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

type Module = {
    id: string;
    title: string;
    description: string;
    image?: string | null;
};

interface ModulesGridProps {
    limit?: number;
    showHero?: boolean;
    className?: string;
    showOnHomePageOnly?: boolean;
    limitImagesToFirstThree?: boolean;
    /** SSG: pre-fetched modules (skip client fetch when provided) */
    initialModules?: Module[];
}

const INITIAL_IMAGE_ROWS = 5;
const ITEMS_PER_ROW = 3; // Assuming 3 columns (xl:grid-cols-3)

export function ModulesGrid({ limit, showHero = false, className, showOnHomePageOnly = false, limitImagesToFirstThree = false, initialModules }: ModulesGridProps) {
    const locale = useLocale();
    const t = useTranslations('modules');
    const [modules, setModules] = useState<Module[]>(() => (initialModules?.length ? (limit ? initialModules.slice(0, limit) : initialModules) : []));
    const [isLoading, setIsLoading] = useState(!initialModules?.length);
    const [showAllImages, setShowAllImages] = useState(false);

    useEffect(() => {
        if (initialModules?.length) {
            setModules(limit ? initialModules.slice(0, limit) : initialModules);
            setIsLoading(false);
            return;
        }
        const fetchModules = async () => {
            try {
                setIsLoading(true);
                const url = showOnHomePageOnly
                    ? `/api/modules?locale=${locale}&showOnHomePage=true`
                    : `/api/modules?locale=${locale}`;
                const response = await fetch(url, {
                    cache: 'no-store',
                });
                if (response.ok) {
                    const data = await response.json();
                    const fetchedModules = Array.isArray(data) ? data : [];
                    setModules(limit ? fetchedModules.slice(0, limit) : fetchedModules);
                } else {
                    setModules([]);
                }
            } catch (error) {
                setModules([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchModules();
    }, [locale, limit, showOnHomePageOnly, initialModules?.length]);

    // Map modules from API to card format
    const cards = useMemo(() => {
        return modules.map((module) => ({
            id: module.id,
            imageSrc: module.image || undefined,
            imageAlt: module.title,
            title: module.title,
            description: module.description,
            href: getLocalizedPagePath('mlm-software-modules', locale),
        }));
    }, [modules, locale]);

    if (isLoading) {
        return (
            <div className={`grid gap-6 md:grid-cols-2 xl:grid-cols-3 ${className || ''}`}>
                {[...Array(limit || 6)].map((_, index) => (
                    <div
                        key={`skeleton-${index}`}
                        className="h-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"
                    />
                ))}
            </div>
        );
    }

    if (cards.length === 0) {
        return (
            <div className={`text-center py-12 ${className || ''}`}>
                <p className="text-gray-500 dark:text-gray-400 text-lg">
                    {t('noModulesAvailable')}
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
                    {t('modulesWillAppear')}
                </p>
            </div>
        );
    }

    // Calculate initial image limit: 5 rows * 3 items per row = 15 items
    const initialImageLimit = INITIAL_IMAGE_ROWS * ITEMS_PER_ROW;
    const hasMoreThanInitialRows = cards.length > initialImageLimit;
    const shouldShowLoadMoreButton = hasMoreThanInitialRows && !showAllImages;

    return (
        <>
            <div className={`grid gap-6 md:grid-cols-2 xl:grid-cols-3 ${className || ''}`}>
                {cards.map((card, index) => {
                    const href = getLocalizedPath(card.href, locale);

                    // Determine if image should be shown
                    let imageSrc: string | undefined;
                    if (limitImagesToFirstThree) {
                        // Original behavior: only show images for first 3 items
                        imageSrc = index >= 3 ? undefined : card.imageSrc;
                    } else {
                        // New behavior: show images for first 5 rows (15 items) initially,
                        // then show all images after "Load More" is clicked
                        if (showAllImages) {
                            imageSrc = card.imageSrc;
                        } else {
                            imageSrc = index < initialImageLimit ? card.imageSrc : undefined;
                        }
                    }

                    return (
                        <Card
                            key={card.id}
                            title={card.title}
                            description={card.description}
                            href={href}
                            imageSrc={imageSrc}
                            imageAlt={card.imageAlt}
                            index={index}
                            showIndex={false}
                            showFooterLink={false}
                            animationDelay={index * 80}
                            imageHeight="h-40 md:h-48"
                            imageLinkable={false}
                        />
                    );
                })}
            </div>

            {shouldShowLoadMoreButton && (
                <div className="mt-8 flex justify-center">
                    <button
                        onClick={() => setShowAllImages(true)}
                        className={cn(
                            'group/button relative inline-flex items-center gap-2 font-medium rounded-lg transition-all duration-300',
                            'border-2 border-primary-500 dark:border-primary-400 bg-transparent text-primary-600 dark:text-primary-400',
                            'hover:bg-primary-700 dark:hover:bg-primary-700 hover:text-white dark:hover:text-white',
                            'hover:border-primary-700 dark:hover:border-primary-700 hover:shadow-lg hover:shadow-primary-500/30 hover:-translate-y-0.5',
                            'px-10 py-4 text-lg',
                            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
                        )}
                        aria-label={t('loadMore', { default: 'Load More' })}
                    >
                        <span>{t('loadMore', { default: 'Load More' })}</span>
                        <span
                            aria-hidden="true"
                            className="transition-all duration-300 group-hover/button:translate-x-1"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                                />
                            </svg>
                        </span>
                    </button>
                </div>
            )}
        </>
    );
}

